import { Users, Clock, Flame, Award, ChefHat } from "lucide-react";

const RecipeDetail = ({ recipe, onBack }) => {
  const m = recipe.macros_per_serving || {};
  const cmp = recipe.macros_comparison;
  const ingredients = recipe.ingredients || [];
  const instructions = recipe.instructions || [];
  const shoppingList = recipe.shopping_list || [];
  const subs = recipe.substitutions || recipe.suggested_swaps || [];
  const prepTips = recipe.meal_prep_tips || [];
  const hasVariants =
    !!recipe.lower_calorie_version ||
    !!recipe.higher_protein_version ||
    !!recipe.meal_prep_version;

  return (
    <div data-testid="recipe-detail">
      <button
        onClick={onBack}
        className="text-white/60 hover:text-white text-xs tracking-[0.2em] uppercase mb-6 inline-flex items-center gap-2"
        data-testid="detail-back"
      >
        ← Back
      </button>

      <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
        <div className="border border-[#1A1A1A] bg-black overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-full">
          {recipe.image_data_url ? (
            <img src={recipe.image_data_url} alt={recipe.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ChefHat size={80} className="text-white/10" />
            </div>
          )}
        </div>

        <div>
          {recipe.cuisine && (
            <p className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-2">
              {recipe.cuisine}
            </p>
          )}
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl leading-none mb-3 tracking-tight"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            data-testid="detail-title"
          >
            {recipe.title}
          </h1>
          <p className="text-white/70 text-sm mb-6 leading-relaxed">
            {recipe.description}
          </p>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6">
            <Stat icon={<Users size={14} />} label="Servings" value={recipe.servings || "—"} />
            <Stat icon={<Clock size={14} />} label="Prep" value={`${recipe.prep_minutes || 0}m`} />
            <Stat icon={<Clock size={14} />} label="Cook" value={`${recipe.cook_minutes || 0}m`} />
            <Stat icon={<Flame size={14} />} label="Cal" value={m.calories || "—"} />
            <Stat icon={<Award size={14} />} label="Protein" value={`${m.protein_g || 0}g`} />
            <Stat icon={<Award size={14} />} label="Fiber" value={`${m.fiber_g || 0}g`} />
          </div>

          {cmp && <MacroComparison cmp={cmp} />}

          <Section title="Ingredients" testid="detail-ingredients">
            <IngredientsList items={ingredients} />
          </Section>

          <Section title="Instructions" testid="detail-instructions">
            <InstructionsList steps={instructions} />
          </Section>

          {shoppingList.length > 0 && (
            <Section title="Shopping list" testid="detail-shopping">
              <ShoppingList items={shoppingList} />
            </Section>
          )}

          {subs.length > 0 && (
            <Section title="Substitutions" testid="detail-subs">
              <SubsList items={subs} />
            </Section>
          )}

          {prepTips.length > 0 && (
            <Section title="Meal-prep tips" testid="detail-prep">
              <PrepList items={prepTips} />
            </Section>
          )}

          {recipe.storage && (
            <Section title="Storage" testid="detail-storage">
              <p className="text-white/75 text-sm leading-relaxed">{recipe.storage}</p>
            </Section>
          )}

          {hasVariants && <Variants recipe={recipe} />}
        </div>
      </div>
    </div>
  );
};

const IngredientsList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((it, i) => (
      <li key={i} className="text-white/80 text-sm flex gap-2">
        <span className="text-[#D4AF37] flex-shrink-0">•</span>
        <span>
          <span className="text-white font-medium">{it.amount}</span> {it.name}
          {it.notes && <span className="text-white/45 italic"> — {it.notes}</span>}
        </span>
      </li>
    ))}
  </ul>
);

const InstructionsList = ({ steps }) => (
  <ol className="space-y-2.5">
    {steps.map((s, i) => (
      <li key={i} className="text-white/80 text-sm flex gap-3">
        <span className="flex-shrink-0 w-6 h-6 border border-[#D4AF37]/50 bg-black text-[#D4AF37] text-xs font-bold flex items-center justify-center">
          {i + 1}
        </span>
        <span className="pt-0.5">{s}</span>
      </li>
    ))}
  </ol>
);

const ShoppingList = ({ items }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((it, i) => (
      <span key={i} className="border border-white/10 text-white/70 text-xs px-2.5 py-1">
        {it}
      </span>
    ))}
  </div>
);

const SubsList = ({ items }) => (
  <ul className="space-y-1.5 text-white/75 text-sm">
    {items.map((s, i) => (
      <li key={i}>
        <span className="text-[#D4AF37]">↔</span> {s.swap}
        {s.reason && <span className="text-white/45 italic"> — {s.reason}</span>}
      </li>
    ))}
  </ul>
);

const PrepList = ({ items }) => (
  <ul className="space-y-1.5 text-white/75 text-sm">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2">
        <span className="text-[#D4AF37]">•</span>
        {t}
      </li>
    ))}
  </ul>
);

const MacroComparison = ({ cmp }) => {
  const original = cmp.original || {};
  const recreated = cmp.recreated || {};
  return (
    <div className="border border-[#25D366]/30 bg-[#25D366]/[0.05] p-4 mb-6">
      <p className="text-[#25D366] text-[11px] tracking-[0.3em] uppercase font-bold mb-2">
        Macro Comparison
      </p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-white/50 text-[10px] uppercase">Original</p>
          <p className="text-white/80">
            {original.calories || 0} kcal · {original.protein_g || 0}g P
          </p>
        </div>
        <div>
          <p className="text-white/50 text-[10px] uppercase">Recreated</p>
          <p className="text-white">
            {recreated.calories || 0} kcal · {recreated.protein_g || 0}g P
          </p>
        </div>
      </div>
      {(cmp.calorie_savings || cmp.protein_increase_g) && (
        <p className="text-[#D4AF37] text-xs mt-2 font-semibold">
          Save {cmp.calorie_savings || 0} kcal · +{cmp.protein_increase_g || 0}g protein
        </p>
      )}
    </div>
  );
};

const Variants = ({ recipe }) => (
  <Section title="Variants" testid="detail-variants">
    <div className="space-y-3 text-sm">
      {recipe.lower_calorie_version && (
        <p>
          <span className="text-[#D4AF37] font-semibold">Lower calorie: </span>
          <span className="text-white/75">{recipe.lower_calorie_version}</span>
        </p>
      )}
      {recipe.higher_protein_version && (
        <p>
          <span className="text-[#D4AF37] font-semibold">Higher protein: </span>
          <span className="text-white/75">{recipe.higher_protein_version}</span>
        </p>
      )}
      {recipe.meal_prep_version && (
        <p>
          <span className="text-[#D4AF37] font-semibold">Meal-prep version: </span>
          <span className="text-white/75">{recipe.meal_prep_version}</span>
        </p>
      )}
    </div>
  </Section>
);

const Stat = ({ icon, label, value }) => (
  <div className="border border-white/10 bg-black/40 p-2 text-center">
    <div className="flex items-center justify-center gap-1 text-[#D4AF37]">{icon}</div>
    <p className="text-white text-xs font-bold mt-1">{value}</p>
    <p className="text-white/40 text-[9px] uppercase tracking-wider">{label}</p>
  </div>
);

const Section = ({ title, testid, children }) => (
  <div className="mt-6" data-testid={testid}>
    <p className="text-[#D4AF37] text-[11px] tracking-[0.3em] uppercase font-bold mb-3">
      {title}
    </p>
    {children}
  </div>
);

export default RecipeDetail;
